import { useCallback, useMemo } from "react";

import { collectActions } from "~/providers/store/collect/actions";
import {
  useCollectGroupPrimary,
  useCollectGroupSecondary
} from "~/providers/store/collect/hooks";

import { type GroupFilterOption } from "./group-filter";
import {
  buildGroupNameToPrimary,
  filterByGroupSelection,
  getPrimaryOptions,
  getSecondaryOptions
} from "./selector-filter";

/** One rendered layer of the selector filter (feeds a GroupFilter.Layer). */
export type SelectorFilterLayer = {
  /** Stable key + column heading, taken from the layer's name. */
  key: string;
  label: string;
  options: GroupFilterOption[];
  selected: string | null;
  onSelect: (value: string | null) => void;
};

export type SelectorFilter = {
  /** The 1–2 layers to render; empty when there's no usable taxonomy. */
  layers: SelectorFilterLayer[];
  /** Whether the filter is worth showing at all. */
  isActive: boolean;
  /** A stable key describing the current selection, e.g. for resetting pagination. */
  selectionKey: string;
  /** Whether any layer currently has a selection. */
  hasSelection: boolean;
  /** Clears every layer (e.g. for a clear-filters button). */
  reset: () => void;
  /** Narrows any list of grouped items by the active selection. */
  filter: <T extends { groupName: string }>(items: T[]) => T[];
};

/** The server-shaped group taxonomy, from the POS school endpoint. */
export type SelectorTaxonomy = {
  layers: string[];
  firstLayerOptions: string[];
  optionsByFirstLayer: Record<string, string[]>;
};

/**
 * Drives a 1–2 layer group filter from the server-provided taxonomy —
 * lexicon-agnostic: layer headings are the layer names, and years/classes are
 * just the first/second layer. Owns the selection state and returns the `layers`
 * to feed a `GroupFilter` plus a `filter` to narrow the grid.
 */
export function useSelectorFilter({
  layers: layerNames,
  firstLayerOptions,
  optionsByFirstLayer
}: SelectorTaxonomy): SelectorFilter {
  const selectedPrimary = useCollectGroupPrimary();
  const selectedSecondary = useCollectGroupSecondary();

  // GroupFilter renders at most two layers, so schools with 3+ selectors are
  // unsupported (disabled below rather than rendering a broken filter).
  const isSupported = layerNames.length >= 1 && layerNames.length <= 2;

  const primaryOptions = useMemo(
    () => getPrimaryOptions(firstLayerOptions),
    [firstLayerOptions]
  );
  const secondaryOptions = useMemo(
    () => getSecondaryOptions(optionsByFirstLayer, selectedPrimary),
    [optionsByFirstLayer, selectedPrimary]
  );
  const groupNameToPrimary = useMemo(
    () => buildGroupNameToPrimary(optionsByFirstLayer),
    [optionsByFirstLayer]
  );

  const filter = useCallback(
    <T extends { groupName: string }>(items: T[]): T[] =>
      filterByGroupSelection(
        items,
        groupNameToPrimary,
        selectedPrimary,
        selectedSecondary
      ),
    [groupNameToPrimary, selectedPrimary, selectedSecondary]
  );

  const reset = collectActions.resetGroupFilter;

  const layers = useMemo<SelectorFilterLayer[]>(() => {
    // Unsupported taxonomy (3+ layers): render no filter.
    if (!isSupported) {
      return [];
    }

    const [firstLayerName, secondLayerName] = layerNames;
    if (!firstLayerName) {
      return [];
    }

    const result: SelectorFilterLayer[] = [
      {
        key: firstLayerName,
        label: firstLayerName,
        options: primaryOptions,
        selected: selectedPrimary,
        // GroupFilter clears the secondary when the primary changes.
        onSelect: collectActions.setGroupPrimary
      }
    ];

    if (secondLayerName) {
      result.push({
        key: secondLayerName,
        label: secondLayerName,
        options: secondaryOptions,
        selected: selectedSecondary,
        onSelect: collectActions.setGroupSecondary
      });
    }

    return result;
  }, [
    isSupported,
    layerNames,
    primaryOptions,
    secondaryOptions,
    selectedPrimary,
    selectedSecondary
  ]);

  const selectionKey =
    layers.map((layer) => layer.selected ?? "all").join(":") || "all";
  const hasSelection = selectedPrimary !== null || selectedSecondary !== null;

  // A real choice exists with several primaries, or a second layer to narrow by
  // even when there's only one primary.
  const isActive =
    isSupported && (primaryOptions.length > 1 || layerNames.length === 2);

  return {
    layers,
    isActive,
    selectionKey,
    hasSelection,
    reset,
    filter
  };
}
